import { useSegmentNavigate } from '@integrations/segment';
import * as api from '@lib/api';
import { isEmployee } from '@lib/auth';
import { useResetState } from '@src/state';
import { useAuthorization } from '@vacasa/react-components-lib';
import React, { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ScrollToTop } from './components/scroll-to-top';
import { EstimatorPath } from './constants';
import { emitStepAnalytics, useAnalytics } from './lib/analytics';
import { saveUTMsToSession } from './lib/util';
import EnterAddress from './steps/enter-address';
import GatherPropertyDetails from './steps/gather-property-details';
import GatherUserDetails from './steps/gather-user-details';
import NeedMoreDetails from './steps/need-more-details';
import ThankYou from './steps/thank-you';

const StepRouter: React.FC<{
    scrollTarget: React.RefObject<HTMLDivElement>;
}> = ({ scrollTarget }) => {
    const auth = useAuthorization();
    const analytics = useAnalytics();
    const navigate = useSegmentNavigate();
    const location = useLocation();
    const resetState = useResetState();

    useEffect(() => {
        if (!isEmployee(auth)) {
            api.emitAnalytics('begin');
        }
    }, []);

    saveUTMsToSession();

    const onNext = (path: EstimatorPath) => {
        emitStepAnalytics(path, 'start', analytics);
        navigate(path);
    };

    const onBack = (path: EstimatorPath) => {
        emitStepAnalytics(location.pathname.slice(1) as EstimatorPath, 'back', analytics);
        emitStepAnalytics(path, 'start', analytics);
        navigate(path);
    };

    const onError = () => {
        const path = EstimatorPath.ThankYou;
        emitStepAnalytics(path, undefined);
        navigate(path);
    };

    const onRetry = () => {
        const path = EstimatorPath.Search;
        api.emitAnalytics('try-different-address', analytics);
        emitStepAnalytics(path, 'start');
        resetState();
        navigate(path);
    };

    const onComplete = onError; //with removal of rent projection, same as onError

    return (
        <>
            <ScrollToTop />
            <Routes>
                <Route
                    index
                    element={
                        <EnterAddress
                            onNext={() => onNext(EstimatorPath.AddressDetails)}
                            scrollTarget={scrollTarget}
                        />
                    }
                />
                <Route
                    path={EstimatorPath.Search}
                    element={
                        <EnterAddress
                            onNext={() => onNext(EstimatorPath.AddressDetails)}
                            scrollTarget={scrollTarget}
                        />
                    }
                />
                <Route
                    path={EstimatorPath.AddressDetails}
                    element={
                        <GatherPropertyDetails
                            onBack={() => onBack(EstimatorPath.Search)}
                            onNext={() => onNext(EstimatorPath.UserDetails)}
                            scrollTarget={scrollTarget}
                        />
                    }
                />
                <Route
                    path={EstimatorPath.UserDetails}
                    element={
                        <GatherUserDetails
                            onBack={() => onBack(EstimatorPath.AddressDetails)}
                            onNext={onComplete}
                            onError={onError}
                            scrollTarget={scrollTarget}
                        />
                    }
                />
                <Route
                    path={EstimatorPath.Error}
                    element={<NeedMoreDetails scrollTarget={scrollTarget} onRetry={onRetry} />}
                />
                <Route
                    path={EstimatorPath.ThankYou}
                    element={<ThankYou scrollTarget={scrollTarget} />}
                />
            </Routes>
        </>
    );
};

export default StepRouter;
